/**
 * STREAK PROTECTOR — PURE logic for consumable that rescues a missed day.
 *
 * Domain concept, INDEPENDENT of ads: a protector is a token
 * in user inventory that, spent on (habit, day), causes streak
 * engine to treat that day as PRESERVED instead of broken. Acquisition
 * sources are pluggable (`ProtectorSource`); today `premium`/`admin_grant`,
 * and `rewarded_ad` plugs in during ads wave without touching this file.
 *
 * ⚠️ INTERPRETATION SEALING. Deciding if a day can be rescued requires knowing
 * if THAT DAY was scheduled — resolved against definition ACTIVE
 * on that date (`resolveDefinitionAt`), NEVER against live definition.
 * Otherwise, changing a habit schedule today would re-judge the past: days that
 * were never due would appear rescuable (and conversely, genuinely lost days
 * would stop being so). Therefore `isDayProtectable` receives timeline
 * and resolves, while `canApplyProtector` receives resolved verdict.
 */

import { addDays, diffDays, isScheduledOn, toDayKey, weekStart } from "./schedule";
import type { CompletionMap, HabitGoal, HabitSchedule } from "./types";
import { type HabitDefinitionTimeline, resolveDefinitionAt } from "./version-timeline";

/** Registered acquisition sources. `rewarded_ad` arrives with ads wave. */
export const PROTECTOR_SOURCES = ["admin_grant", "premium", "rewarded_ad"] as const;
export type ProtectorSource = (typeof PROTECTOR_SOURCES)[number];

/**
 * ANTI-FARMING caps. A protector is "a life": if accumulated without
 * limit, streak loses meaning and consumable loses value
 * (and with rewarded ads, reward would lack a ceiling).
 */
/** Maximum UNSPENT protectors held at one time. */
export const PROTECTOR_INVENTORY_CAP = 3;
/** Maximum protectors ACQUIRED per calendar day (any source). */
export const PROTECTOR_EARNED_PER_DAY_CAP = 1;
/**
 * Rescue window: only a missed day within last N days can be
 * protected. Without window, consumable would rewrite entire history in one
 * stroke — exactly what interpretation sealing exists to prevent.
 */
export const PROTECTOR_RESCUE_WINDOW_DAYS = 7;

/**
 * ACTIVE protector caps (CONFIG_OVER_CODE).
 *
 * Server resolves them (`app_config` → `GET /api/config`) and client
 * REFLECTS them: transported here only. Omitting policy = historical
 * literals, so default behavior is identical to past.
 *
 * ⚠️ **Changing these caps CANNOT re-judge past.** They decide what is
 * offered for rescue going forward; whether a day was SCHEDULED is answered by
 * definition active on THAT day (`resolveDefinitionAt`, interpretation sealing),
 * and an already protected day remains protected even if window is subsequently narrowed —
 * `protectedDaysByHabit` intentionally inspects no caps.
 */
export interface ProtectorPolicy {
  inventoryCap: number;
  earnedPerDayCap: number;
  rescueWindowDays: number;
}

/** Historical literals: default when no resolved policy exists. */
export const DEFAULT_PROTECTOR_POLICY: ProtectorPolicy = {
  inventoryCap: PROTECTOR_INVENTORY_CAP,
  earnedPerDayCap: PROTECTOR_EARNED_PER_DAY_CAP,
  rescueWindowDays: PROTECTOR_RESCUE_WINDOW_DAYS,
};

/** `streak_protector` row as received from PowerSync bucket (SQLite). */
export interface StreakProtectorRow {
  id: string;
  /** Rescued habit; `null` ⇒ protector remains in inventory. */
  habit_id?: string | null;
  /** Rescued day (day-key); `null` ⇒ unspent. */
  date?: string | null;
  source?: string | null;
  granted_at?: string | null;
  applied_at?: string | null;
  deleted_at?: string | null;
}

/** Reasons why rescue CANNOT proceed (translated by UI). */
export type ProtectorRejection =
  | "no-inventory"
  | "not-past"
  | "out-of-window"
  | "before-history"
  | "not-scheduled"
  | "already-resolved"
  | "already-protected";

export interface ProtectorDecision {
  allowed: boolean;
  reason: ProtectorRejection | null;
}

const ALLOWED: ProtectorDecision = { allowed: true, reason: null };

function deny(reason: ProtectorRejection): ProtectorDecision {
  return { allowed: false, reason };
}

function isLive(row: StreakProtectorRow): boolean {
  return !row.deleted_at;
}

/** Active and UNSPENT protectors (available inventory). */
export function availableProtectors(rows: readonly StreakProtectorRow[]): StreakProtectorRow[] {
  return rows.filter((row) => isLive(row) && !row.habit_id && !row.date);
}

/** Available inventory size. */
export function inventoryCount(rows: readonly StreakProtectorRow[]): number {
  return availableProtectors(rows).length;
}

/** Protectors ACQUIRED on a calendar day (by `granted_at`). */
export function earnedOn(rows: readonly StreakProtectorRow[], dayKey: string): number {
  return rows.filter((row) => {
    if (!isLive(row) || !row.granted_at) {
      return false;
    }
    const at = new Date(row.granted_at);
    return !Number.isNaN(at.getTime()) && toDayKey(at) === dayKey;
  }).length;
}

/**
 * Can user OBTAIN another protector now? Client mirror of server-side
 * guard (`db/streak_protector.rs`): full inventory or daily cap ⇒ no.
 */
export function canEarnProtector(
  rows: readonly StreakProtectorRow[],
  todayKey: string,
  policy: ProtectorPolicy = DEFAULT_PROTECTOR_POLICY,
): { allowed: boolean; reason: "inventory-full" | "daily-cap" | null } {
  if (inventoryCount(rows) >= policy.inventoryCap) {
    return { allowed: false, reason: "inventory-full" };
  }
  if (earnedOn(rows, todayKey) >= policy.earnedPerDayCap) {
    return { allowed: false, reason: "daily-cap" };
  }
  return { allowed: true, reason: null };
}

/** Protected days per habit (to feed streak engine). */
export function protectedDaysByHabit(
  rows: readonly StreakProtectorRow[],
): Map<string, Set<string>> {
  const byHabit = new Map<string, Set<string>>();
  for (const row of rows) {
    if (!isLive(row) || !row.habit_id || !row.date) {
      continue;
    }
    const days = byHabit.get(row.habit_id);
    if (days) {
      days.add(row.date);
    } else {
      byHabit.set(row.habit_id, new Set([row.date]));
    }
  }
  return byHabit;
}

/** Protected days for SINGLE habit (empty if none). */
export function protectedDaysFor(
  rows: readonly StreakProtectorRow[],
  habitId: string,
): ReadonlySet<string> {
  return protectedDaysByHabit(rows).get(habitId) ?? new Set<string>();
}

/**
 * First day STREAK ENGINE examines for this habit, or `undefined` if
 * habit lacks history.
 *
 * Exact mirror of `streaks.ts`: `dailyStreaks` scans from oldest
 * check-in, and `weeklyStreaks` from that check-in's week. A day
 * PRIOR to that point is never visited by engine ⇒ protecting it affects neither
 * active streak nor record: wasted protector.
 *
 * Without check-ins **there is no streak to save** (`current = 0`), so there is
 * nothing to rescue — case of a newly created habit.
 */
export function habitHistoryStart(
  completions: CompletionMap,
  schedule: HabitSchedule,
): string | undefined {
  let min: string | undefined;
  for (const key of completions.keys()) {
    if (min === undefined || key < min) {
      min = key;
    }
  }
  if (min === undefined) {
    return undefined;
  }
  // In `times_per_week` unit is the WEEK: a day protected before
  // first check-in but within same week DOES count towards that week
  // (`weeklyCounts` counts it), so boundary is Monday of that week.
  return schedule.kind === "times_per_week" ? weekStart(min) : min;
}

/**
 * Rescue decision with schedule verdict ALREADY RESOLVED by
 * caller (see `isDayProtectable`, which resolves against definition
 * timeline). Pure and dependency-free: easily testable case-by-case.
 */
export function canApplyProtector(args: {
  /** Candidate day for rescue. */
  dayKey: string;
  /** Hoy (day-key local). */
  todayKey: string;
  /** Was that day scheduled according to definition ACTIVE ON THAT DAY? */
  scheduledOnThatDay: boolean;
  /** Already checked-in (done/skipped)? Nothing to rescue. */
  resolved: boolean;
  /** Already has a protector? */
  alreadyProtected: boolean;
  /** Protectores disponibles en el inventario. */
  available: number;
  /**
   * First day with habit history (`habitHistoryStart`). `undefined` ⇒
   * habit has no check-ins: no streak to save.
   */
  historyStart?: string | undefined;
  /** Active rescue window. Omitted ⇒ historical default (7 days). */
  rescueWindowDays?: number;
}): ProtectorDecision {
  if (args.alreadyProtected) {
    return deny("already-protected");
  }
  // TODAY is not rescued: today unmarked does not yet break anything (pending
  // until end of day). Rescuing it would waste a protector.
  if (args.dayKey >= args.todayKey) {
    return deny("not-past");
  }
  if (
    diffDays(args.dayKey, args.todayKey) > (args.rescueWindowDays ?? PROTECTOR_RESCUE_WINDOW_DAYS)
  ) {
    return deny("out-of-window");
  }
  // Before habit had history, engine does not check: protecting there
  // saves no streak. A habit WITHOUT check-ins has nothing to rescue.
  if (args.historyStart === undefined || args.dayKey < args.historyStart) {
    return deny("before-history");
  }
  if (!args.scheduledOnThatDay) {
    return deny("not-scheduled");
  }
  if (args.resolved) {
    return deny("already-resolved");
  }
  if (args.available <= 0) {
    return deny("no-inventory");
  }
  return ALLOWED;
}

/**
 * Can `dayKey` be rescued for this habit? RESOLVES schedule against
 * definition timeline (interpretation sealing) and delegates
 * decision to `canApplyProtector`.
 *
 * Legacy habit (without versions) resolves to live definition ⇒
 * identical behavior to past.
 */
export function isDayProtectable(args: {
  timeline: HabitDefinitionTimeline;
  currentGoal: HabitGoal;
  currentSchedule: HabitSchedule;
  completions: CompletionMap;
  protectedDays: ReadonlySet<string>;
  available: number;
  dayKey: string;
  todayKey: string;
  policy?: ProtectorPolicy;
}): ProtectorDecision {
  const { schedule } = resolveDefinitionAt(
    args.timeline,
    args.currentGoal,
    args.currentSchedule,
    args.dayKey,
  );
  return canApplyProtector({
    dayKey: args.dayKey,
    todayKey: args.todayKey,
    scheduledOnThatDay: isScheduledOn(schedule, args.dayKey),
    resolved: args.completions.has(args.dayKey),
    alreadyProtected: args.protectedDays.has(args.dayKey),
    available: args.available,
    rescueWindowDays: (args.policy ?? DEFAULT_PROTECTOR_POLICY).rescueWindowDays,
    // Boundary is measured with LIVE schedule (unit — day or week — is
    // that of habit today, which engine uses when computing streak).
    historyStart: habitHistoryStart(args.completions, args.currentSchedule),
  });
}

/**
 * Rescue window days that this habit can recover, from most
 * recent to oldest. Consumed by UI to offer rescue.
 *
 * A habit **without history** (newly created, zero check-ins) returns `[]`: no
 * streak to save. Offering "rescue Tuesday" there would sell a protector
 * — and with rewarded ads, an impression — for NOTHING.
 */
export function rescuableDays(args: {
  timeline: HabitDefinitionTimeline;
  currentGoal: HabitGoal;
  currentSchedule: HabitSchedule;
  completions: CompletionMap;
  protectedDays: ReadonlySet<string>;
  todayKey: string;
  policy?: ProtectorPolicy;
}): string[] {
  const days: string[] = [];
  const window = (args.policy ?? DEFAULT_PROTECTOR_POLICY).rescueWindowDays;
  for (let i = 1; i <= window; i += 1) {
    const dayKey = addDays(args.todayKey, -i);
    // `available: 1` on purpose: question here is "IS this day rescuable?",
    // not "does user have means?". Inventory is checked by action.
    const decision = isDayProtectable({ ...args, dayKey, available: 1 });
    if (decision.allowed) {
      days.push(dayKey);
    }
  }
  return days;
}
